import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import LoadingSkeleton from './components/LoadingSkeleton';
import ErrorBoundary from './components/ErrorBoundary';
import { initializeAnalytics, trackPageView } from './utils/analytics';
import './App.css';
import './themes/black-theme.css';
import './themes/shared-styles.css';

// Lazy load page components for better performance
const Home = lazy(() => import('./pages/Home'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const Tables = lazy(() => import('./pages/Tables'));
const MapPage = lazy(() => import('./pages/MapPage'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const FriendsPage = lazy(() => import('./pages/FriendsPage'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'));
const StatusPage = lazy(() => import('./pages/StatusPage'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const VerifySMS = lazy(() => import('./pages/VerifySMS'));
const BrandGuidelines = lazy(() => import('./pages/BrandGuidelines'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));

// Component to handle analytics initialization and page tracking
function AppContent() {
  const location = useLocation();

  // Initialize analytics once on mount
  useEffect(() => {
    initializeAnalytics();
  }, []);

  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return (
    <div className="app">
      <Navbar />
      <main className="main-content">
        <ErrorBoundary>
          <Suspense fallback={
            <div className="loading-container">
              <LoadingSkeleton type="card" count={3} />
            </div>
          }>
            <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<AuthPage mode="login" />} />
            <Route path="/register" element={<AuthPage mode="register" />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/verify-sms" element={<VerifySMS />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/tables" element={<Tables />} />
            <Route path="/map" element={<MapPage />} />
            <Route
              path="/friends"
              element={
                <ProtectedRoute>
                  <FriendsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/:id"
              element={
                <ProtectedRoute>
                  <UserProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedback"
              element={
                <ProtectedRoute>
                  <FeedbackPage />
                </ProtectedRoute>
              }
            />
            <Route path="/status" element={<StatusPage />} />
            <Route path="/brand" element={<BrandGuidelines />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminPage />
                </ProtectedRoute>
              }
            />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
